import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';

const PROMO_CODE_TYPES = [
  { value: 'events', label: 'Events' },
  { value: 'classes', label: 'Classes' },
  { value: 'packs', label: 'Packs' },
];

interface PromoCodeFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  promoCode?: any;
}

const PromoCodeFormDialog: React.FC<PromoCodeFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  promoCode,
}) => {
  const isEditMode = !!promoCode;

  const [formData, setFormData] = useState({
    code: '',
    value: '',
    valueCap: '',
    type: 'amount',
    isActive: true,
    maxUsage: '',
    expiresAt: '',
    promoCodeType: '',
    studio: '',
    event: '',
    session: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [studios, setStudios] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);

  const fetchStudios = useCallback(async () => {
    try {
      const res = await apiService.getStudios({});
      setStudios(res?.data || res?.studios || []);
    } catch (e) {
      console.error('Error fetching studios:', e);
    }
  }, []);

  // Helper function to filter upcoming events/sessions
  const filterUpcoming = useCallback((items: any[], dateField: string = 'date', selectedId?: string) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    return items.filter((item) => {
      // Always include the selected item (for editing past events/sessions)
      if (selectedId && item._id === selectedId) return true;
      
      if (!item[dateField]) return false;
      const itemDate = new Date(item[dateField]);
      itemDate.setHours(0, 0, 0, 0);
      return itemDate >= now;
    }).sort((a, b) => {
      // Sort by date ascending
      const dateA = new Date(a[dateField]).getTime();
      const dateB = new Date(b[dateField]).getTime();
      return dateA - dateB;
    });
  }, []);

  const fetchEvents = useCallback(async (studioId?: string, selectedEventId?: string) => {
    try {
      const res = studioId
        ? await apiService.getStudioEvents(studioId, { pageSize: 300 })
        : await apiService.getEvents({ pageSize: 300 });
      const allEvents = res?.events || res?.data || [];
      // Filter to only show upcoming events (or selected event if editing)
      const upcomingEvents = filterUpcoming(allEvents, 'date', selectedEventId);
      setEvents(upcomingEvents);
    } catch (e) {
      console.error('Error fetching events:', e);
      setEvents([]);
    }
  }, [filterUpcoming]);

  const fetchSessions = useCallback(async (studioId: string, selectedSessionId?: string) => {
    try {
      const res = await apiService.getStudioSessions(studioId, {});
      const allSessions = res?.sessions || [];
      // Filter to only show upcoming sessions (or selected session if editing)
      const upcomingSessions = filterUpcoming(allSessions, 'date', selectedSessionId);
      setSessions(upcomingSessions);
    } catch (e) {
      console.error('Error fetching sessions:', e);
      setSessions([]);
    }
  }, [filterUpcoming]);

  useEffect(() => {
    if (open) fetchStudios();
  }, [open, fetchStudios]);

  useEffect(() => {
    if (!open) return;
    if (formData.promoCodeType === 'events') {
      fetchEvents(formData.studio || undefined, formData.event || undefined);
    } else {
      setEvents([]);
    }
  }, [open, formData.promoCodeType, formData.studio, formData.event, fetchEvents]);

  useEffect(() => {
    if (!open) return;
    if (formData.promoCodeType === 'classes' && formData.studio) {
      fetchSessions(formData.studio, formData.session || undefined);
    } else {
      setSessions([]);
    }
  }, [open, formData.promoCodeType, formData.studio, formData.session, fetchSessions]);

  useEffect(() => {
    if (open) {
      if (promoCode) {
        const studioId = typeof promoCode.studio === 'object' && promoCode.studio?._id
          ? promoCode.studio._id
          : promoCode.studio || '';
        const eventId = typeof promoCode.event === 'object' && promoCode.event?._id
          ? promoCode.event._id
          : promoCode.event || '';
        const sessionId = typeof promoCode.session === 'object' && promoCode.session?._id
          ? promoCode.session._id
          : promoCode.session || '';
        setFormData({
          code: promoCode.code || '',
          value: promoCode.value?.toString() || '',
          valueCap: promoCode.valueCap?.toString() || '',
          type: promoCode.type || 'amount',
          isActive: promoCode.isActive !== undefined ? promoCode.isActive : true,
          maxUsage: promoCode.maxUsage?.toString() || '',
          expiresAt: promoCode.expiresAt
            ? new Date(promoCode.expiresAt).toISOString().split('T')[0]
            : '',
          promoCodeType: promoCode.promoCodeType || '',
          studio: studioId,
          event: eventId,
          session: sessionId,
        });
      } else {
        resetForm();
      }
    }
  }, [open, promoCode]);

  const resetForm = () => {
    setFormData({
      code: '',
      value: '',
      valueCap: '',
      type: 'amount',
      isActive: true,
      maxUsage: '',
      expiresAt: '',
      promoCodeType: '',
      studio: '',
      event: '',
      session: '',
    });
    setError(null);
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'promoCodeType') {
        next.studio = '';
        next.event = '';
        next.session = '';
      } else if (field === 'studio') {
        next.event = '';
        next.session = '';
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!formData.code.trim()) {
        throw new Error('Code is required');
      }

      if (!formData.value || parseFloat(formData.value) < 0) {
        throw new Error('Value must be a positive number');
      }

      // Global promo codes are not supported; scope is required
      if (!formData.promoCodeType) {
        throw new Error('Scope is required (Events / Classes / Packs)');
      }

      const payload: any = {
        code: formData.code.trim().toUpperCase(),
        value: parseFloat(formData.value),
        type: formData.type,
        isActive: formData.isActive,
      };

      if (formData.valueCap) {
        payload.valueCap = parseFloat(formData.valueCap);
      }

      // Handle maxUsage - send number if valid, otherwise don't send (unlimited)
      if (formData.maxUsage && formData.maxUsage.trim() !== '' && parseInt(formData.maxUsage) > 0) {
        payload.maxUsage = parseInt(formData.maxUsage);
      }
      // If maxUsage is empty and we're editing, we don't send it (backend will keep existing value)
      // To make it unlimited, user would need to explicitly set it to 0 or clear and save

      if (formData.expiresAt) {
        payload.expiresAt = new Date(formData.expiresAt);
      }

      // promoCodeType is required (no global support)
        payload.promoCodeType = formData.promoCodeType;

      if (formData.studio) {
        payload.studio = formData.studio;
      } else if (isEditMode) {
        payload.studio = null;
      }

      if (formData.event) {
        payload.event = formData.event;
      } else if (isEditMode) {
        payload.event = null;
      }

      if (formData.session) {
        payload.session = formData.session;
      } else if (isEditMode) {
        payload.session = null;
      }

      if (isEditMode) {
        await apiService.updatePromoCode(promoCode._id, payload);
      } else {
        await apiService.createPromoCode(payload);
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {isEditMode ? 'Edit Promo Code' : 'Create Promo Code'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Code *"
            value={formData.code}
            onChange={(e) => {
              // Remove spaces from input
              const valueWithoutSpaces = e.target.value.replace(/\s/g, '');
              handleChange('code', valueWithoutSpaces.toUpperCase());
            }}
            fullWidth
            disabled={isEditMode}
            helperText="Promo code will be converted to uppercase (no spaces allowed)"
            error={formData.code.includes(' ')}
          />

          <FormControl fullWidth required>
            <InputLabel>Scope (Promo Code Type) *</InputLabel>
            <Select
              value={formData.promoCodeType}
              label="Scope (Promo Code Type) *"
              onChange={(e) => handleChange('promoCodeType', e.target.value)}
            >
              {PROMO_CODE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Required: choose Events / Classes / Packs (Global is not supported)
            </Typography>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Studio</InputLabel>
            <Select
              value={formData.studio}
              label="Studio"
              onChange={(e) => handleChange('studio', e.target.value)}
            >
              <MenuItem value="">None (Global)</MenuItem>
              {studios.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.studioName || s._id}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Optional: Restrict to a specific studio
            </Typography>
          </FormControl>

          {formData.promoCodeType === 'events' && (
            <Autocomplete
              options={events}
              getOptionLabel={(option) => {
                if (!option || typeof option === 'string') return option || '';
                const eventName = option.eventName || option.title || option._id || '';
                const date = option.date ? new Date(option.date).toLocaleDateString() : '';
                return date ? `${eventName} - ${date}` : eventName;
              }}
              value={events.find((ev) => ev._id === formData.event) || null}
              onChange={(_, newValue) => {
                handleChange('event', newValue ? newValue._id : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event"
                  placeholder="Search or select an event"
                  helperText="Optional: Restrict to a specific upcoming event"
                />
              )}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              noOptionsText="No upcoming events found"
            />
          )}

          {formData.promoCodeType === 'classes' && formData.studio && (
            <Autocomplete
              options={sessions}
              getOptionLabel={(option) => {
                if (!option || typeof option === 'string') return option || '';
                const className = option.classType?.title || option.className || 'Session';
                const date = option.date ? new Date(option.date).toLocaleDateString('en-GB') : '';
                return date ? `${className} - ${date}` : className;
              }}
              value={sessions.find((s) => s._id === formData.session) || null}
              onChange={(_, newValue) => {
                handleChange('session', newValue ? newValue._id : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Session (Class)"
                  placeholder="Search or select a session"
                  helperText="Optional: Restrict to a specific upcoming session"
                />
              )}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              noOptionsText="No upcoming sessions found"
            />
          )}

          <FormControl fullWidth>
            <InputLabel>Type *</InputLabel>
            <Select
              value={formData.type}
              label="Type *"
              onChange={(e) => handleChange('type', e.target.value)}
            >
              <MenuItem value="amount">Amount (₹)</MenuItem>
              <MenuItem value="percentage">Percentage (%)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={`Value * (${formData.type === 'percentage' ? 'Percentage' : 'Amount in ₹'})`}
            type="number"
            value={formData.value}
            onChange={(e) => handleChange('value', e.target.value)}
            onWheel={stopWheelNudge}
            fullWidth
            inputProps={{
              min: 0,
              step: formData.type === 'percentage' ? 1 : 0.01,
            }}
            helperText={
              formData.type === 'percentage'
                ? 'Enter discount percentage (0-100)'
                : 'Enter discount amount in rupees'
            }
          />

          {formData.type === 'percentage' && (
            <TextField
              label="Maximum Discount Cap (₹)"
              type="number"
              value={formData.valueCap}
              onChange={(e) => handleChange('valueCap', e.target.value)}
              onWheel={stopWheelNudge}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Optional: Maximum discount amount in rupees"
            />
          )}

          <TextField
            label="Maximum Usage"
            type="number"
            value={formData.maxUsage}
            onChange={(e) => handleChange('maxUsage', e.target.value)}
            onWheel={stopWheelNudge}
            fullWidth
            inputProps={{ min: 1 }}
            helperText="Optional: Leave empty for unlimited usage"
          />

          <TextField
            label="Expiry Date"
            type="date"
            value={formData.expiresAt}
            onChange={(e) => handleChange('expiresAt', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Optional: Leave empty for no expiry"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.code || !formData.value}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoCodeFormDialog;

